import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, ClipboardCheck, Mail, Send, X } from "lucide-react";
import { toast } from "sonner";

export default function ClosuresPage() {
  const { t } = useTranslation();
  const [technicians, setTechnicians] = useState([]);
  const [filterTech, setFilterTech] = useState("all");
  const [items, setItems] = useState([]);
  const [emailDialog, setEmailDialog] = useState({ open: false, closureId: null, mode: "customer" });
  const [emailTo, setEmailTo] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  const loadHistory = () => api.get("/closures/email-history").then((r) => setEmailHistory(r.data)).catch(() => {});

  const load = async () => {
    const params = filterTech !== "all" ? { technician_id: filterTech } : {};
    const { data } = await api.get("/closures", { params });
    setItems(data);
  };
  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechnicians(r.data));
    loadHistory();
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTech]);

  const downloadPdf = async (id) => {
    const res = await api.get(`/closures/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chiusura_${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEmail = (id, mode) => {
    setEmailDialog({ open: true, closureId: id, mode });
    setEmailTo("");
    setReplyTo("");
    setEmailNote("");
  };

  const sendEmail = async () => {
    if (!emailTo.includes("@")) return toast.error("Inserisci un'email valida");
    setEmailBusy(true);
    try {
      const r = await api.post(`/closures/${emailDialog.closureId}/email`, {
        to: emailTo.trim(),
        reply_to: replyTo.trim() || null,
        mode: emailDialog.mode,
        extra_note: emailNote || null,
      });
      toast.success(`Email inviata a ${r.data.sent_to}`);
      setEmailDialog({ open: false, closureId: null, mode: "customer" });
      loadHistory();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    } finally {
      setEmailBusy(false);
    }
  };

  const removeHistoryEmail = async (email) => {
    try {
      await api.delete(`/closures/email-history/${encodeURIComponent(email)}`);
      loadHistory();
    } catch {}
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Archivio</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.closure_reports")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("auto.registrazioni_delle_chiusure_impianto_firmate_dal_cliente_co")}</p>
      </div>

      <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white p-4">
        <Label className="text-xs text-slate-500">{t("auto.tecnico")}</Label>
        <Select value={filterTech} onValueChange={setFilterTech}>
          <SelectTrigger className="w-64 rounded-sm" data-testid="closures-filter-tech"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("auto.tutti")}</SelectItem>
            {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">{t("common.date")}</th>
              <th className="p-3">{t("auto.rif_impianto")}</th>
              <th className="p-3">{t("common.customer")}</th>
              <th className="p-3">{t("common.technician")}</th>
              <th className="p-3">{t("auto.foto")}</th>
              <th className="p-3">{t("auto.firma")}</th>
              <th className="p-3 text-right">Verbale</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("auto.nessuna_chiusura_ancora_registrata")}</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-mono text-xs">{(c.closed_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 font-mono text-xs">{c.intervention_ref || "—"}</td>
                <td className="p-3">{c.customer_name || "—"}</td>
                <td className="p-3">{c.technician_name || "—"}</td>
                <td className="p-3 font-mono text-xs">{(c.photos_before || []).length + (c.photos_after || []).length}</td>
                <td className="p-3">
                  {c.signature_file_id ? <ClipboardCheck className="h-4 w-4 text-emerald-600" /> : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEmail(c.id, "assistant")} className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`email-assistant-${c.id}`} title="Invia agli assistenti (completo, con foto/seriali/firma)">
                      <Mail className="mr-1 h-3 w-3" /> Ufficio
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEmail(c.id, "customer")} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`email-customer-${c.id}`} title="Invia al cliente (solo rapportino)">
                      <Send className="mr-1 h-3 w-3" /> Cliente
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(c.id)} className="rounded-sm" data-testid={`pdf-closure-${c.id}`}>
                      <FileText className="mr-1 h-3 w-3" /> PDF
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog Invio Email */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && setEmailDialog({ open: false, closureId: null, mode: "customer" })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {emailDialog.mode === "assistant" ? <Mail className="h-5 w-5 text-blue-600" /> : <Send className="h-5 w-5 text-emerald-600" />}
              {emailDialog.mode === "assistant" ? "Invia all'ufficio (completo)" : "Invia al cliente (solo rapportino)"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm bg-slate-50 p-2 text-xs text-slate-600">
              {emailDialog.mode === "assistant"
                ? "L'email includerà: dettagli cliente, indirizzo, seriali installati, note, link alle foto pre/post e firma cliente."
                : "L'email al cliente conterrà: intervento, data, tecnico, indirizzo, seriali installati e note. Nessuna foto o firma interna."}
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                list="email-history-list"
                placeholder="cliente@example.com"
                className="rounded-sm"
                data-testid="email-to"
              />
              <datalist id="email-history-list">
                {emailHistory.map((h) => <option key={h.email} value={h.email}>{h.email} ({h.uses}x)</option>)}
              </datalist>
              {emailHistory.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  {emailHistory.slice(0, 6).map((h) => (
                    <span key={h.email} className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-2 py-0.5">
                      <button type="button" onClick={() => setEmailTo(h.email)} className="text-slate-700 hover:text-blue-600">{h.email}</button>
                      <button type="button" onClick={() => removeHistoryEmail(h.email)} title="Rimuovi dalla cronologia" className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Reply-to (opzionale, es. tua email personale)</Label>
              <Input
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="mario.tecnico@example.com"
                className="rounded-sm"
                data-testid="email-reply-to"
              />
              <div className="mt-0.5 text-[10px] text-slate-500">Se il destinatario risponde all'email, la risposta arriva qui.</div>
            </div>
            <div>
              <Label className="text-xs">Nota extra (opzionale)</Label>
              <textarea
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                rows={2}
                placeholder="Es. rimango a disposizione per eventuali chiarimenti…"
                className="w-full rounded-sm border border-slate-200 p-2 text-sm"
                data-testid="email-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, closureId: null, mode: "customer" })} className="rounded-sm">Annulla</Button>
            <Button onClick={sendEmail} disabled={emailBusy || !emailTo.includes("@")} className={`rounded-sm ${emailDialog.mode === "assistant" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="email-send">
              {emailBusy ? "Invio…" : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
